import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import Seo from '../components/Seo';

const API_BASE_URL = 'https://api.funizzle.com';
const DELETE_ACCOUNT_ENDPOINT = `${API_BASE_URL}/account-deletion/request`;

const REASONS = [
  'I no longer use this account',
  'Privacy concerns',
  'I have another account',
  'App experience issues',
  'Other',
];

export default function DeleteAccount() {
  const [form, setForm] = useState({ name: '', email: '', reason: REASONS[0] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState(false);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setOk(false);

    if (!form.name.trim()) {
      setError('Please enter your full name.');
      return;
    }

    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
    if (!isValidEmail) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(DELETE_ACCOUNT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          reason: form.reason.trim(),
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.message || 'Unable to submit your request right now.');
      }

      setOk(true);
      setForm({ name: '', email: '', reason: REASONS[0] });
    } catch (submitError) {
      setError(submitError.message || 'Unable to submit your request right now.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo
        path="/delete-account"
        title="Request Account Deletion"
        description="Request deletion of your BingePlay account and data."
        noindex
      />
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#f4f7fb',
          py: { xs: 4, md: 7 },
          px: 2,
        }}
      >
        <Box
          sx={{
            maxWidth: 980,
            mx: 'auto',
            backgroundColor: '#fff',
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(90deg, #14D8CE 0%, #ff4d4f 100%)',
              color: '#fff',
              px: { xs: 3, md: 5 },
              py: { xs: 3, md: 4 },
            }}
          >
            <Typography sx={{ fontFamily: 'Rammetto One', fontSize: { xs: 24, md: 34 } }}>
              Delete Account Request
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mt: 1 }}>Last updated: May 6, 2026</Typography>
          </Box>

          <Box sx={{ px: { xs: 3, md: 5 }, py: { xs: 3, md: 5 }, color: '#1f2937' }}>
            <Typography sx={{ fontFamily: 'Poppins', mb: 2 }}>
              If you want to delete your BingePlay account, submit the request form below. Our team
              will email you for confirmation before permanent deletion.
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2, mb: 3 }}>
              <TextField
                label="Full Name"
                value={form.name}
                onChange={handleChange('name')}
                fullWidth
                required
                sx={{ mb: 2 }}
              />
              <TextField
                label="Email Address"
                type="email"
                value={form.email}
                onChange={handleChange('email')}
                fullWidth
                required
                sx={{ mb: 2 }}
              />
              <TextField
                select
                label="Reason"
                value={form.reason}
                onChange={handleChange('reason')}
                fullWidth
                sx={{ mb: 2 }}
              >
                {REASONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  bgcolor: '#ff4d4f',
                  textTransform: 'none',
                  fontFamily: 'Poppins',
                  px: 3,
                  py: 1.2,
                  '&:hover': { bgcolor: '#df383a' },
                }}
                startIcon={loading ? <CircularProgress color="inherit" size={18} /> : null}
              >
                {loading ? 'Submitting...' : 'Delete My Account'}
              </Button>
            </Box>

            <Typography sx={{ fontFamily: 'Rammetto One', fontSize: 18, mb: 1.5, mt: 2 }}>
              What happens next?
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              1. We review your request and send a confirmation email to verify identity.
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              2. After confirmation, account and associated profile/activity data are scheduled for
              deletion.
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              3. Most data is deleted within 30 days. Some records may be retained where legally
              required (for example financial/security logs).
            </Typography>
            <Typography sx={{ fontFamily: 'Poppins', mb: 1 }}>
              4. For help, contact support@funizzle.com.
            </Typography>
          </Box>
        </Box>
        <Snackbar open={ok} autoHideDuration={3500} onClose={() => setOk(false)}>
          <Alert severity="success" variant="filled" onClose={() => setOk(false)}>
            Request received. Please check your email for confirmation steps.
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
}
